import React, { useEffect, useState, useCallback, useRef } from "react";
import "./../../../../assets/css/purchase/form.css";

import InitialDataFormLayout from "../components/InitialDataFormLayout";
import {
  labelStyle,
  inputStyle,
  selectStyle,
  sectionHeaderStyle,
  sectionIconStyle,
  sectionTitleStyle,
} from "../components/formStyles";

import {
  deleteData,
  getAlert,
  insertData,
  parseCurrency,
  updateData,
  getData,
} from "../../../../utils/SamuiUtils";

const DEFAULT_FORM = {
  comp_id: parseCurrency(window.localStorage.getItem("company"), 10),
  Group_Id: 0,
  Group_Code: "",
  Group_Name: "",
  Group_Status: "Y",
  Doc_Type: null,
};

const ItemGroupForm = ({
  callInitialize,
  mode,
  name,
  formData,
  setFormData,
  selectedData,
}) => {
  const [initialFormData, setInitialFormData] = useState(DEFAULT_FORM);
  const [isSaving, setIsSaving] = useState(false);
  const isSavingRef = useRef(false);

  useEffect(() => {
    if (mode === "I") {
      setFormData(DEFAULT_FORM);
      setInitialFormData(DEFAULT_FORM);
    } else if (mode === "U" && selectedData) {
      const editData = {
        comp_id: parseCurrency(selectedData.Comp_Id, 10),
        Group_Id: parseCurrency(selectedData.Group_Id, 10),
        Group_Code: selectedData.Group_Code || "",
        Group_Name: selectedData.Group_Name || "",
        Group_Status: selectedData.Group_Status || "Y",
        Doc_Type: null,
      };
      setFormData(editData);
      setInitialFormData(editData);
    }
  }, [mode, selectedData, setFormData]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleInsert = useCallback(async () => {
    try {
      const existing = await getData("Tb_Set_ItmGroup", "Group_Status = 'Y'");
      const newCode = (formData.Group_Code || "").trim().toLowerCase();
      const isDuplicate = Array.isArray(existing) && existing.some((r) => (r.Group_Code || "").trim().toLowerCase() === newCode);
      if (isDuplicate) {
        getAlert("FAILED", "รหัสหมวดหมู่สินค้านี้ถูกใช้งานอยู่แล้ว");
        return;
      }

      const { Group_Id, ...dataToInsert } = formData;
      const fields = Object.keys(dataToInsert).join(", ");
      const values = Object.values(dataToInsert);
      await insertData("Tb_Set_ItmGroup", fields, values);
      await callInitialize();
      setFormData(DEFAULT_FORM);
      setInitialFormData(DEFAULT_FORM);
    } catch (error) {
      console.error("Error inserting data:", error);
      getAlert(
        "FAILED",
        "ไม่สามารถบันทึกข้อมูลได้ เนื่องจากมีข้อมูลนี้อยู่ในระบบแล้ว"
      );
    }
  }, [formData, callInitialize, setFormData]);

  const handleUpdate = useCallback(async () => {
    try {
      const existing = await getData("Tb_Set_ItmGroup", "Group_Status = 'Y'");
      const newCode = (formData.Group_Code || "").trim().toLowerCase();
      const isDuplicate = Array.isArray(existing) && existing.some(
        (r) => (r.Group_Code || "").trim().toLowerCase() === newCode && r.Group_Id !== formData.Group_Id
      );
      if (isDuplicate) {
        getAlert("FAILED", "รหัสหมวดหมู่สินค้านี้ถูกใช้งานอยู่แล้ว");
        return;
      }

      const where = { Group_Id: formData.Group_Id };
      const { Group_Id, ...values } = formData;
      const res = await updateData("Tb_Set_ItmGroup", values, where);
      if (res) {
        await callInitialize();
      }
    } catch (error) {
      getAlert(
        "FAILED",
        "ไม่สามารถอัปเดตข้อมูลได้ เนื่องจากมีข้อมูลนี้อยู่ในระบบแล้ว"
      );
    }
  }, [formData, callInitialize]);

  const handleDelete = async () => {
    if (!selectedData?.Group_Id) {
      getAlert("WARNING", "ไม่พบข้อมูลที่ต้องการลบ");
      return;
    }

    getAlert("C_DIALOG", "ยืนยันการลบข้อมูล", async (isConfirm) => {
      if (isConfirm) {
        await deleteData(
          "Tb_Set_ItmGroup",
          `WHERE Group_Id = ${selectedData.Group_Id}`
        );
        await callInitialize();
      }
    });
  };

  const handleSubmit = useCallback(async () => {
    if (isSavingRef.current) return;
    if (!formData.Group_Code || !formData.Group_Name) {
      getAlert("WARNING", "โปรดกรอกรหัสและชื่อหมวดหมู่สินค้า");
      return;
    }

    isSavingRef.current = true;
    setIsSaving(true);
    try {
      if (mode === "I") {
        await handleInsert();
      } else if (mode === "U") {
        await handleUpdate();
      }
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }
  }, [formData.Group_Code, formData.Group_Name, mode, handleInsert, handleUpdate]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (
        event.target.tagName === "INPUT" ||
        event.target.tagName === "TEXTAREA" ||
        event.target.tagName === "SELECT"
      ) {
        return;
      }

      if (event.key === "Escape") {
        event.preventDefault();
        getAlert("C_DIALOG", "ต้องการยกเลิกการทำงานหรือไม่?", (isConfirm) => {
          if (isConfirm) {
            callInitialize();
          }
        });
      }

      if (event.key === "F10") {
        event.preventDefault();
        if (!isSavingRef.current) handleSubmit();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleSubmit, callInitialize]);

  const handleReset = () => {
    setFormData(initialFormData);
  };

  const handleBack = () => {
    callInitialize();
  };

  return (
    <InitialDataFormLayout
      name={name}
      entityPath="/tbsetitmgroup"
      mode={mode}
      onSubmit={handleSubmit}
      isSubmittingDisabled={isSaving}
      onBack={handleBack}
      onReset={handleReset}
      onDelete={mode === "U" ? handleDelete : undefined}
      callInitialize={callInitialize}
    >
      <div style={sectionHeaderStyle}>
        <i className="fas fa-tags" style={sectionIconStyle}></i>
        <h5 style={sectionTitleStyle}>ข้อมูลหมวดหมู่สินค้า</h5>
      </div>
      <div className="row g-4">
        <div className="col-md-4">
          <label style={{ ...labelStyle, whiteSpace: 'nowrap' }}>รหัสหมวดหมู่สินค้า</label>
          <input
            type="text"
            className="form-control"
            name="Group_Code"
            value={formData.Group_Code || ""}
            onChange={handleChange}
            style={inputStyle}
          />
        </div>
        <div className="col-md-4">
          <label style={{ ...labelStyle, whiteSpace: 'nowrap' }}>ชื่อหมวดหมู่สินค้า</label>
          <input
            type="text"
            className="form-control"
            name="Group_Name"
            value={formData.Group_Name || ""}
            onChange={handleChange}
            style={inputStyle}
          />
        </div>
        <div className="col-md-4">
          <label style={labelStyle}>สถานะ</label>
          <select
            className="form-select"
            name="Group_Status"
            value={formData.Group_Status || "Y"}
            onChange={handleChange}
            style={selectStyle}
          >
            <option value="Y">ใช้งาน</option>
            <option value="N">ไม่ใช้งาน</option>
          </select>
        </div>
      </div>
    </InitialDataFormLayout>
  );
};

export default ItemGroupForm;
